import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;
const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export interface ApiKey {
    _id: string;
    name: string;
    prefix: string;
    createdAt: string;
    lastUsedAt?: string;
    usageCount: number;
    revokedAt?: string;
    revokedBy?: string;
    createdBy?: string;
}

export interface AdminApiKey extends ApiKey {
    userId: string;
    lastUsedIp?: string;
    revokeReason?: string;
    user: { _id: string; email: string; name?: string; company?: string } | null;
    recentCalls: number;
    recentErrors: number;
}

export interface ApiKeyUsage {
    days: number;
    daily: { day: string; calls: number }[];
    routes: { method?: string; route?: string; calls: number; errors: number; lastAt: string }[];
    ips: { ip?: string; calls: number; lastAt: string }[];
}

const errorMessage = (e: any) => e?.response?.data?.error || "Une erreur est survenue";

// ── Mine (Settings) ───────────────────────────────────────────────────────────

export const getMyApiKeys = async () => {
    const res = await axios.get("/api-keys", config());
    return res.data as { keys: ApiKey[]; maxActive: number };
};

export const revealApiKey = async (id: string): Promise<{ secret?: string; error?: string }> => {
    try {
        const res = await axios.get(`/api-keys/${id}/secret`, config());
        return res.data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const revokeMyApiKey = async (id: string): Promise<{ key?: ApiKey; error?: string }> => {
    try {
        const res = await axios.delete(`/api-keys/${id}`, config());
        return res.data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

// ── Back-office ───────────────────────────────────────────────────────────────

export const getAllApiKeys = async (status: "all" | "active" | "revoked", q?: string) => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    const res = await axios.get(`/admin/api-keys?${params}`, config());
    return res.data as { keys: AdminApiKey[]; usageDays: number };
};

export const getApiKeyUsage = async (id: string) => {
    const res = await axios.get(`/admin/api-keys/${id}/usage`, config());
    return res.data as ApiKeyUsage;
};

/**
 * Creates a key for a user (only admins can): the user picked in the list (userId) or typed (email, matched
 * exactly by the server). `secret` is shown once here; the user can also show it in Settings.
 */
export const adminCreateApiKey = async (
    owner: { userId: string } | { email: string },
    name: string
): Promise<{ key?: AdminApiKey; secret?: string; error?: string }> => {
    try {
        const res = await axios.post("/admin/api-keys", { ...owner, name }, config());
        return res.data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const adminRevokeApiKey = async (id: string, reason?: string): Promise<{ error?: string }> => {
    try {
        await axios.post(`/admin/api-keys/${id}/revoke`, { reason }, config());
        return {};
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const adminRestoreApiKey = async (id: string): Promise<{ error?: string }> => {
    try {
        await axios.post(`/admin/api-keys/${id}/restore`, {}, config());
        return {};
    } catch (e) {
        return { error: errorMessage(e) };
    }
};
